import { Check } from "lucide-react";
import { FaqSection } from "@/components/FaqSection";
import { LangLink } from "@/components/LangLink";
import { useLanguage } from "@/lib/language";
import { content, whatsappUrl } from "@/lib/content";
import type { Industry } from "@/lib/industries";
import { EditorialHero } from "./EditorialHero";
import { BuyerGuides } from "./BuyerGuides";
import { isoServicePath } from "@/lib/vendors";

const labels = {
  eyebrow: { en: "Industries We Serve", ar: "القطاعات التي نخدمها" },
  back: { en: "Industries", ar: "القطاعات" },
  whyTitle: { en: "Why it matters", ar: "الأهمية" },
  standardsTitle: { en: "Standards to consider", ar: "مواصفات يمكن النظر فيها" },
};

export function IndustryPage({ industry }: { industry: Industry }) {
  const { t } = useLanguage();

  return (
    <>
      <EditorialHero>
        <LangLink to="/industries" className="ic-text-link">
          {t(labels.back)}
        </LangLink>
        <p className="ic-eyebrow mt-7">{t(labels.eyebrow)}</p>
        <h1 className="ic-title">{t(industry.title)}</h1>
        <p className="ic-intro">{t(industry.description)}</p>
        <a
          href={`${whatsappUrl}?text=${encodeURIComponent(t({ en: `Hello IraqCert, I would like to discuss management-system preparation for ${industry.name.en}.`, ar: `مرحباً عراق سيرت، أود مناقشة التحضير لنظام الإدارة في مجال ${industry.name.ar}.` }))}`}
          target="_blank"
          rel="noopener noreferrer"
          className="ic-button mt-7"
        >
          {t({ en: "Discuss your activity", ar: "ناقش نشاط شركتك" })}
        </a>
      </EditorialHero>
      {/* INTRO (BLUF) */}
      <section className="mx-auto max-w-3xl px-4 lg:px-8 py-16 text-start">
        <p className="text-lg leading-relaxed text-muted-foreground">{t(industry.intro)}</p>
        <p className="mt-4 text-base leading-relaxed text-muted-foreground">
          {t({ en: "Explore support for ", ar: "تعرّف على خدمات دعم " })}
          <LangLink
            to={industry.relatedService}
            className="font-semibold text-primary hover:underline"
          >
            {t(industry.relatedLabel)}
          </LangLink>
          {t({ en: ".", ar: "." })}
        </p>
      </section>

      {/* WHY IT MATTERS */}
      <section className="mx-auto max-w-3xl px-4 lg:px-8 pb-8 text-start">
        <h2 className="text-2xl font-extrabold text-foreground sm:text-3xl">
          {t(labels.whyTitle)}
        </h2>
        <div className="mt-8 grid gap-4">
          {industry.whyPoints.map((point) => (
            <div
              key={point.lead.en}
              className="flex items-start gap-3 rounded-2xl border border-slate-100 bg-card p-6 shadow-soft"
            >
              <span className="mt-0.5 inline-flex size-6 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
                <Check className="size-3.5" />
              </span>
              <p className="text-sm leading-relaxed text-foreground/90">
                <strong className="font-bold text-foreground">{t(point.lead)} </strong>
                <span className="text-muted-foreground">{t(point.body)}</span>
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* STANDARDS */}
      <section className="mx-auto max-w-3xl px-4 lg:px-8 pb-16 text-start">
        <h2 className="text-lg font-bold text-foreground">{t(labels.standardsTitle)}</h2>
        <div className="mt-4 flex flex-wrap gap-2">
          {industry.standards.map((iso) => {
            const path = isoServicePath(iso);
            return path ? (
              <LangLink
                key={iso}
                to={path}
                className="rounded-full border border-primary/30 bg-primary/10 px-3 py-1.5 text-xs font-semibold text-primary transition-colors hover:bg-primary hover:text-primary-foreground"
              >
                {iso}
              </LangLink>
            ) : (
              <span
                key={iso}
                className="rounded-full border border-border px-3 py-1.5 text-xs font-semibold text-muted-foreground"
              >
                {iso}
              </span>
            );
          })}
        </div>
      </section>

      <BuyerGuides />
      {/* FAQ */}
      <FaqSection items={industry.faq} className="bg-gradient-to-b from-background to-accent/40" />

      {/* CTA */}
      <section className="bg-gradient-to-b from-accent/40 to-accent">
        <div className="mx-auto max-w-3xl px-4 lg:px-8 py-20 text-center">
          <h2 className="text-3xl font-extrabold text-foreground sm:text-4xl">
            {t(content.industries.cta.title)}
          </h2>
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="group mt-8 inline-flex items-center gap-2 rounded-full bg-primary px-8 py-3.5 text-base font-semibold text-primary-foreground shadow-soft transition-all hover:brightness-110"
          >
            {t(content.industries.cta.button)}
          </a>
        </div>
      </section>
    </>
  );
}
